/**
 * 공용 로딩 스켈레톤 — 데이터가 오기 전에 "올 내용과 같은 윤곽"을 회색 뼈대로 깔아 둔다.
 *
 * 왜 글자("불러오는 중…") 대신 뼈대인가
 *  1) 레이아웃이 덜컹 뛰지 않는다. 한 줄짜리 문구가 30줄짜리 표로 바뀌면 그 순간 화면 전체가
 *     밀리고, 사용자가 누르려던 버튼이 다른 자리로 도망간다. 뼈대는 자리를 미리 잡아 둔다.
 *  2) 얼마나 올지 감이 잡힌다. 뼈대 12줄이면 "표가 오겠구나", 카드 6장이면 "카드가 오겠구나".
 *
 * 쓰는 법 — 자리의 모양에 맞는 것을 고른다. 만능 하나로 때우면 카드 자리에 표가 깔린다.
 *   표(tbody 안)  <SkeletonTableRows rows={8} cols={6} />
 *   목록/행 나열   <SkeletonList rows={6} />
 *   카드 그리드    <SkeletonCards count={6} />
 *   글 문단        <SkeletonText lines={3} />
 *   입력 폼        <SkeletonForm fields={5} />
 *   패널 한 덩어리 <SkeletonPanel />
 *   화면 전체      <SkeletonPage />
 *
 * 🚫 버튼 글씨·콤보박스 드롭다운 한 줄처럼 "글자 자리"에는 쓰지 않는다 — 거기는 글자가 맞다.
 */
import type { CSSProperties, ReactNode } from "react";

/** 뼈대 한 칸. 애니메이션은 index.css 의 `pb-skeleton` 이 맡는다(움직임 줄이기 설정 존중). */
export function Skeleton({
  className = "",
  style,
}: {
  className?: string;
  style?: CSSProperties;
}) {
  return <div className={`pb-skeleton ${className}`} style={style} aria-hidden="true" />;
}

/** 줄마다 시작 위상을 조금씩 어긋내 물결치듯 보이게 한다(정책 리베이트 표에서 가져온 감각). */
function waveDelay(i: number): CSSProperties {
  return { animationDelay: `${(i % 6) * 0.08}s` };
}

/** 로딩 뼈대임을 보조기기에 알리는 껍데기. 화면에는 아무것도 더 그리지 않는다. */
function SkeletonRoot({
  children,
  label = "불러오는 중",
  className = "",
}: {
  children: ReactNode;
  label?: string;
  className?: string;
}) {
  return (
    <div role="status" aria-busy="true" aria-live="polite" className={className}>
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}

/* ───────────────────────── 글 문단 ───────────────────────── */

/** 문단 뼈대. 마지막 줄은 짧게 — 진짜 글이 그렇게 끝나기 때문이다. */
export function SkeletonText({
  lines = 3,
  className = "",
}: {
  lines?: number;
  className?: string;
}) {
  return (
    <SkeletonRoot className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={`h-3.5 ${i === lines - 1 ? "w-2/5" : i % 3 === 1 ? "w-11/12" : "w-full"}`}
          style={waveDelay(i)}
        />
      ))}
    </SkeletonRoot>
  );
}

/* ───────────────────────── 표 ───────────────────────── */

/**
 * 표 본문 뼈대 — `<tbody>` 안에 그대로 넣는다.
 * 표는 `<tr>` 사이에 `<div>` 를 끼울 수 없어 SkeletonRoot 를 못 쓴다. 대신 첫 행에 role 을 준다.
 */
export function SkeletonTableRows({
  rows = 8,
  cols = 5,
  /** 열별 너비 비율(예: [2,1,1,1,3]). 생략하면 첫 열만 넓게. */
  colWidths,
  className = "",
}: {
  rows?: number;
  cols?: number;
  colWidths?: number[];
  className?: string;
}) {
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <tr
          key={r}
          className={className}
          {...(r === 0
            ? { role: "status" as const, "aria-busy": true, "aria-label": "불러오는 중" }
            : {})}
        >
          {Array.from({ length: cols }).map((_, c) => (
            <td key={c} className="px-3 py-3.5">
              <Skeleton
                className="h-3.5"
                style={{
                  ...waveDelay(r + c),
                  width: colWidths
                    ? `${Math.min(100, colWidths[c] * 22)}%`
                    : c === 0
                      ? "70%"
                      : "55%",
                }}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

/* ───────────────────────── 목록 ───────────────────────── */

/** 행이 줄줄이 늘어서는 목록(표가 아닌 것) 뼈대. avatar 를 주면 왼쪽에 동그란 칸을 붙인다. */
export function SkeletonList({
  rows = 6,
  avatar = false,
  className = "",
}: {
  rows?: number;
  avatar?: boolean;
  className?: string;
}) {
  return (
    <SkeletonRoot className={`divide-y divide-gray-100 dark:divide-white/[0.06] ${className}`}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-3">
          {avatar && <Skeleton className="h-9 w-9 shrink-0 rounded-full" style={waveDelay(i)} />}
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-3.5 w-2/5" style={waveDelay(i)} />
            <Skeleton className="h-3 w-3/4 opacity-70" style={waveDelay(i + 1)} />
          </div>
          <Skeleton className="h-3 w-14 shrink-0 opacity-70" style={waveDelay(i + 2)} />
        </div>
      ))}
    </SkeletonRoot>
  );
}

/* ───────────────────────── 카드 ───────────────────────── */

/** 카드 그리드 뼈대. cols 는 넓은 화면 기준 열 수(좁으면 자동으로 줄어든다). */
export function SkeletonCards({
  count = 6,
  cols = 3,
  className = "",
}: {
  count?: number;
  cols?: 1 | 2 | 3 | 4;
  className?: string;
}) {
  const grid = {
    1: "grid-cols-1",
    2: "sm:grid-cols-2",
    3: "sm:grid-cols-2 lg:grid-cols-3",
    4: "sm:grid-cols-2 lg:grid-cols-4",
  }[cols];
  return (
    <SkeletonRoot className={`grid grid-cols-1 gap-4 ${grid} ${className}`}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-white/[0.02]"
        >
          <Skeleton className="h-4 w-1/2" style={waveDelay(i)} />
          <div className="mt-3 space-y-2">
            <Skeleton className="h-3 w-full opacity-70" style={waveDelay(i + 1)} />
            <Skeleton className="h-3 w-4/5 opacity-70" style={waveDelay(i + 2)} />
          </div>
          <Skeleton className="mt-4 h-7 w-24 rounded-lg" style={waveDelay(i + 3)} />
        </div>
      ))}
    </SkeletonRoot>
  );
}

/* ───────────────────────── 입력 폼 ───────────────────────── */

/** 라벨 + 입력칸 쌍이 늘어선 폼 뼈대. 모달이 작게 떴다가 커지는 덜컹거림을 막는다. */
export function SkeletonForm({
  fields = 5,
  className = "",
}: {
  fields?: number;
  className?: string;
}) {
  return (
    <SkeletonRoot className={`space-y-4 ${className}`}>
      {Array.from({ length: fields }).map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-3 w-20 opacity-70" style={waveDelay(i)} />
          <Skeleton className="h-10 w-full rounded-lg" style={waveDelay(i + 1)} />
        </div>
      ))}
    </SkeletonRoot>
  );
}

/* ───────────────────────── 패널 · 화면 ───────────────────────── */

/**
 * 패널 한 덩어리 뼈대 — 제목 한 줄 + 본문 몇 줄.
 * 모양을 특정하기 어려운 자리의 기본값. 적어도 "글자 한 줄"보다는 자리를 잡아 준다.
 */
export function SkeletonPanel({
  lines = 4,
  className = "",
}: {
  lines?: number;
  className?: string;
}) {
  return (
    <SkeletonRoot className={`space-y-3 py-2 ${className}`}>
      <Skeleton className="h-4 w-1/3" />
      <div className="space-y-2">
        {Array.from({ length: lines }).map((_, i) => (
          <Skeleton
            key={i}
            className={`h-3 opacity-70 ${i === lines - 1 ? "w-1/2" : "w-full"}`}
            style={waveDelay(i + 1)}
          />
        ))}
      </div>
    </SkeletonRoot>
  );
}

/**
 * 화면 하나가 통째로 로딩 중일 때(라우트 전환 등).
 * 제목 줄 + 도구 줄 + 표 — 이 프로젝트 화면 대부분이 이 윤곽이라 어느 화면으로 가든 덜 어색하다.
 */
export function SkeletonPage({ className = "" }: { className?: string }) {
  return (
    <SkeletonRoot label="화면을 불러오는 중" className={`p-1 ${className}`}>
      <div className="mb-5 flex items-center justify-between gap-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-9 w-28 rounded-lg" style={waveDelay(1)} />
      </div>
      <div className="mb-4 flex flex-wrap gap-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-24 rounded-lg opacity-70" style={waveDelay(i + 2)} />
        ))}
      </div>
      <div className="overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-800">
        <div className="border-b border-gray-200 bg-gray-50 px-3 py-3 dark:border-gray-800 dark:bg-white/[0.03]">
          <Skeleton className="h-3.5 w-1/4" />
        </div>
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={i}
            className="flex items-center gap-4 border-b border-gray-100 px-3 py-3.5 last:border-b-0 dark:border-white/[0.06]"
          >
            <Skeleton className="h-3.5 w-[22%]" style={waveDelay(i)} />
            <Skeleton className="h-3.5 w-[16%] opacity-70" style={waveDelay(i + 1)} />
            <Skeleton className="h-3.5 w-[14%] opacity-70" style={waveDelay(i + 2)} />
            <Skeleton className="h-3.5 w-[20%] opacity-70" style={waveDelay(i + 3)} />
            <Skeleton className="ml-auto h-3.5 w-[10%] opacity-70" style={waveDelay(i + 4)} />
          </div>
        ))}
      </div>
    </SkeletonRoot>
  );
}

export default Skeleton;
